import { COPIED_ENTITY_SETS, type D365Client } from '@lg/d365-client';
import type { DirectoryClient } from '@lg/directory-client';
import {
  DEFAULT_ENV_PROFILE,
  EXAMPLE_REQUEST_COMPANY,
  MOCK_PEOPLE,
  QUESTION_BY_KEY,
  READINESS_DATA_SETS,
  READINESS_PERSONAS,
  READINESS_SETUP_DATA,
  READINESS_TEMPLATES,
  MASTER_DATA_SHEETS,
  SHEETS,
  TEST_VENUES,
  createRng,
  personaAccount,
  seededVenuePeopleRows,
  demoPeopleRows,
  demoRequests,
  exampleRequestAnswers,
  testVenueAnswers,
  testVenueStaff,
  hasAnyRole,
  maskEin,
  seedTemplateConfigs,
  TEMPLATE_LABELS,
  templateDefaults,
  templateLedgerFor,
  type AppRole,
  type Answers,
  type CurrentUser,
  type DocumentType,
  type EnvProfile,
  type FailureKind,
  type PeopleData,
  type PeopleIssue,
  type PeopleSheet,
  type QuestionContext,
  type ReadinessCase,
  type ReadinessEnv,
  type ReadinessLayer,
  type StepKey,
  type TemplateConfig,
} from '@lg/shared';
import {
  comparableAnswer,
  deviationDetails,
  deviations,
  errorCountBySection,
  validateAnswers,
  withDefaults,
} from '../domain/answers.js';
import {
  PROVISION_PLAN,
  evaluateGate1,
  planNext,
  transition,
  type GateResult,
  type LifecycleAction,
  type LifecycleContext,
} from '../domain/lifecycle.js';
import { ACTIVITIES, type ActivityEnv, type ValidationReport } from './provisioning.js';
import { validateMasterData, type MasterDataContext } from '../domain/people/masterData.js';
import { parsePeopleRows, normaliseRowData, type RawPeopleRow } from '../domain/people/parse.js';
import {
  buildRoutingPeople,
  validatePeople,
  type ExistingPosition,
  type PeopleContext,
} from '../domain/people/validate.js';
import { simulateRouting, type RoutingResult } from '../domain/routing.js';
import {
  buildPeoplePackage,
  peoplePackageEnv,
  buildProjectsPackage,
  buildSetupPackage,
  packageFileName,
  zipPackage,
  type DataPackage,
  type PackageId,
} from '../package/dataPackage.js';
import { buildTemplateLedgerPackage, ledgerOverlaysOf } from '../package/templatePackage.js';
import { templateProfileFor } from '../package/templateProfiles.js';
import {
  buildPeopleWorkbook,
  parsePeopleCsv,
  parsePeopleWorkbook,
  type UploadedRow,
} from '../people/excel.js';
import { samplePeopleRows, type SampleVariant } from '../people/samples.js';
import { buildRequestWorkbook, parseRequestWorkbook } from '../request/workbook.js';
import { buildWorkflowXml, workflowFileName } from '../request/workflowXml.js';
import JSZip from 'jszip';
import { compareRuns, type ComparableResult } from '../readiness/compare.js';
import {
  generateReadinessPlan,
  personaSeats,
  type GovernedSetting,
  type PlanInput,
} from '../readiness/plan.js';
import type {
  AnswerRecord,
  AppData,
  AppDataStore,
  AuditRecord,
  PeopleImportRecord,
  PeopleRowRecord,
  ReadinessPlanRecord,
  ReadinessResultRecord,
  ReadinessRunRecord,
  RequestRecord,
  RunRecord,
  TaskRecord,
  UpgradeCampaignRecord,
} from '../store/records.js';
import type {
  FileDownload,
  IdAvailability,
  PeopleImportSummary,
  PeopleImportView,
  CampaignView,
  ReadinessRunSummary,
  ReadinessView,
  RequestDetail,
  RequestSummary,
  RunView,
  SetupDataStatus,
  TestingLibraryView,
} from './views.js';

export class ServiceError extends Error {
  constructor(
    readonly status: number,
    readonly title: string,
    detail?: string,
  ) {
    super(detail ?? title);
  }
}

export interface AppServiceDeps {
  store: AppDataStore;
  d365: D365Client;
  directory: DirectoryClient;
  now?: () => Date;
  newId?: () => string;
  goLiveLeadDays?: number;
  /** FEATURE_WORKFLOW_IMPORT / FEATURE_POLICY_AUTOMATION (default false → manual tasks) */
  flags?: { workflowImport: boolean; policyAutomation: boolean };
  /** Job polling interval (COPY_POLL_SECONDS) and timeout (COPY_TIMEOUT_MINUTES) */
  pollMs?: number;
  timeoutMs?: number;
  sleep?: (ms: number) => Promise<void>;
  /** How a provisioning run is driven in the background (browser: fire and forget). */
  schedule?: (work: () => Promise<void>) => void;
  /** Provisioning steps that run against the real D365 sandbox (labels only; routing is in the client). */
  liveSteps?: readonly StepKey[];
  /** The D365 environment the live steps target (default: the RSM sandbox) */
  envProfile?: EnvProfile;
}

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const EDITABLE_STATUSES: ReadonlyArray<RequestRecord['status']> = [
  'Draft',
  'Submitted',
  'InReview',
  'Approved',
];

export type PeopleImportSourceInput =
  | { kind: 'sample'; variant: SampleVariant }
  | { kind: 'empty' }
  | { kind: 'rows'; rows: UploadedRow[] }
  | { kind: 'copy'; companyId: string };

/**
 * The application service: every use case the UI needs, with roles enforced
 * here (not in the UI). Runs in the browser for the static demo and inside
 * Azure Functions for the real deployment.
 */
export function createAppService(deps: AppServiceDeps) {
  const now = deps.now ?? (() => new Date());
  const newId = deps.newId ?? (() => crypto.randomUUID());
  const leadDays = deps.goLiveLeadDays ?? 10;
  let data: AppData | null = null;

  // ── store plumbing ─────────────────────────────────────────────────────────

  const flags = deps.flags ?? { workflowImport: false, policyAutomation: false };
  const pollMs = deps.pollMs ?? 30_000;
  const timeoutMs = deps.timeoutMs ?? 120 * 60_000;
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const schedule = deps.schedule ?? ((work: () => Promise<void>) => void work());
  const driving = new Set<string>();
  const liveSteps = deps.liveSteps ?? [];
  const profile = deps.envProfile ?? DEFAULT_ENV_PROFILE;

  function db(): AppData {
    if (!data) {
      data = deps.store.load();
      // Stores written by an earlier build lack the provisioning collections.
      if (data) {
        data.runs ??= [];
        data.steps ??= [];
        data.tasks ??= [];
        data.readinessPlans ??= [];
        data.readinessRuns ??= [];
        data.readinessResults ??= [];
        data.campaigns ??= [];
        // Templates added since this store was seeded (LTPC), and jobs and roles added to the seed
        // catalogs since (GL accountant, finance director, Accountant role). Saved entries
        // are kept as they are; only missing ones are added.
        for (const t of seedTemplateConfigs(new Date(data.seededAsOf))) {
          const saved = data.templates.find((x) => x.id === t.id);
          if (!saved) {
            data.templates.push(t);
            continue;
          }
          for (const j of t.jobCatalog)
            if (!saved.jobCatalog.some((x) => x.jobId === j.jobId)) saved.jobCatalog.push(j);
          for (const r of t.roleCatalog)
            if (!saved.roleCatalog.some((x) => x.roleId === r.roleId)) saved.roleCatalog.push(r);
        }
      }
    }
    if (!data) throw new ServiceError(503, 'Not ready', 'Call ensureSeeded() first');
    return data;
  }
  const commit = (): void => deps.store.save(db());
  const stamp = (): string => now().toISOString();
  const today = (): string => stamp().slice(0, 10);

  function audit(
    actor: string,
    action: string,
    requestId: string | null,
    payload: Record<string, unknown> | null = null,
  ): void {
    const rec: AuditRecord = { id: newId(), requestId, actorUpn: actor, action, payload, at: stamp() };
    db().audit.push(rec);
  }

  function requireRole(user: CurrentUser, roles: readonly AppRole[]): void {
    if (!hasAnyRole(user, roles))
      throw new ServiceError(403, 'Forbidden', `Requires one of: ${roles.join(', ')}`);
  }
  function requireAnyRole(user: CurrentUser): void {
    if (user.roles.length === 0) throw new ServiceError(403, 'Forbidden', 'You have no app role');
  }

  function getRequestRecord(id: string): RequestRecord {
    const r = db().requests.find((x) => x.id === id);
    if (!r) throw new ServiceError(404, 'Not found', `Request ${id} does not exist`);
    return r;
  }
  function template(id: string): TemplateConfig {
    const t = db().templates.find((x) => x.id === id) ?? db().templates.find((x) => x.active);
    if (!t) throw new ServiceError(500, 'No template configured');
    return t;
  }
  function answersOf(requestId: string): Answers {
    const out: Answers = {};
    for (const a of db().answers) if (a.requestId === requestId) out[a.questionKey] = a.value;
    return out;
  }
  function questionContext(req: RequestRecord): QuestionContext {
    return contextFor(req.templateId);
  }
  function contextFor(templateId: string): QuestionContext {
    const tpl = template(templateId);
    return {
      today: today(),
      goLiveLeadDays: leadDays,
      template: tpl,
      defaults: templateDefaults(tpl.id),
      activeTemplates: db()
        .templates.filter((t) => t.active)
        .map((t) => ({ id: t.id, name: `${t.id} (${t.templateLegalEntityId})` })),
      ledger: templateLedgerFor(profile.templateCompany),
    };
  }
  function canEditRequest(user: CurrentUser, req: RequestRecord): boolean {
    return (
      hasAnyRole(user, ['Requester']) &&
      user.upn === req.requesterUpn &&
      EDITABLE_STATUSES.includes(req.status)
    );
  }
  /** Spec 7: any edit after submit returns the request to Draft and invalidates approvals. */
  function markEdited(req: RequestRecord, actor: string): void {
    req.version += 1;
    req.updatedAt = stamp();
    if (req.status !== 'Draft') {
      audit(actor, 'request.returnedToDraft', req.id, { from: req.status, version: req.version });
      req.status = 'Draft';
      req.currentGate = 1;
    }
  }

  function latestImportFor(requestId: string): PeopleImportRecord | null {
    const list = db().peopleImports.filter((i) => i.requestId === requestId);
    return list.sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt))[0] ?? null;
  }
  function rowsOf(importId: string): PeopleRowRecord[] {
    return db().peopleRows.filter((r) => r.importId === importId);
  }
  function importSummary(imp: PeopleImportRecord): PeopleImportSummary {
    const rows = rowsOf(imp.id);
    const issues = [...imp.importIssues, ...rows.flatMap((r) => r.issues)];
    return {
      id: imp.id,
      status: imp.status,
      rowCount: rows.length,
      errorCount: issues.filter((i) => i.severity === 'error').length,
      warningCount: issues.filter((i) => i.severity === 'warning').length,
      validatedAt: imp.validatedAt,
    };
  }
  function importCompanyId(imp: PeopleImportRecord): string {
    if (imp.companyId) return imp.companyId;
    const req = imp.requestId ? getRequestRecord(imp.requestId) : null;
    return req?.legalEntityId ?? 'NEW';
  }
  function canEditImport(user: CurrentUser, imp: PeopleImportRecord): boolean {
    if (imp.requestId) return canEditRequest(user, getRequestRecord(imp.requestId));
    return hasAnyRole(user, ['D365Admin']) && ['Draft', 'Invalid', 'Valid', 'Failed'].includes(imp.status);
  }
  function requireImport(id: string): PeopleImportRecord {
    const imp = db().peopleImports.find((i) => i.id === id);
    if (!imp) throw new ServiceError(404, 'Not found', `People import ${id} does not exist`);
    return imp;
  }

  // ── people validation context (all external lookups, then pure rules) ──────

  async function peopleContext(imp: PeopleImportRecord, rows: RawPeopleRow[]): Promise<PeopleContext> {
    const companyId = importCompanyId(imp);
    const req = imp.requestId ? getRequestRecord(imp.requestId) : null;
    const tpl = template(req?.templateId ?? 'LTPL');
    const mode = imp.requestId ? 'newVenue' : 'existingVenue';
    const { data } = parsePeopleRows(rows);

    const upns = [...new Set(data.users.map((u) => u.upn))];
    const dirEntries = await Promise.all(
      upns.map(async (u) => [u, await deps.directory.getUserByUpn(u)] as const),
    );
    const directory = new Map(
      dirEntries.map(([u, d]) => [u, d ? { displayName: d.displayName, enabled: d.enabled } : null]),
    );

    const existingPositions = new Map<string, ExistingPosition>();
    const toExisting = (p: Awaited<ReturnType<D365Client['getPosition']>>): ExistingPosition | null =>
      p && {
        positionId: p.positionId,
        title: p.title,
        jobId: p.jobId,
        companyId: p.companyId,
        reportsToPositionId: p.reportsToPositionId,
        holder:
          p.holderPersonnelNumber && p.holderName
            ? { name: p.holderName, upn: p.holderUpn, personnelNumber: p.holderPersonnelNumber }
            : null,
      };
    if (mode === 'existingVenue') {
      for (const p of await deps.d365.getPositions(companyId)) {
        const e = toExisting(p);
        if (e) existingPositions.set(e.positionId, e);
      }
    }
    const filePositionIds = new Set(data.positions.map((p) => p.positionId));
    let frontier = [...new Set(data.positions.map((p) => p.reportsToPositionId))].filter(
      (id) => !filePositionIds.has(id) && !existingPositions.has(id),
    );
    for (let depth = 0; frontier.length && depth < 10; depth++) {
      const found = await Promise.all(frontier.map((id) => deps.d365.getPosition(id)));
      const next: string[] = [];
      for (const p of found) {
        const e = toExisting(p);
        if (!e) continue;
        existingPositions.set(e.positionId, e);
        if (e.reportsToPositionId && !existingPositions.has(e.reportsToPositionId))
          next.push(e.reportsToPositionId);
      }
      frontier = [...new Set(next)];
    }
    // Always include the regional/corporate chain above existing positions.
    for (const p of [...existingPositions.values()]) {
      let parent = p.reportsToPositionId;
      for (let depth = 0; parent && !existingPositions.has(parent) && depth < 10; depth++) {
        const e = toExisting(await deps.d365.getPosition(parent));
        if (!e) break;
        existingPositions.set(e.positionId, e);
        parent = e.reportsToPositionId;
      }
    }

    const positionChecks = await Promise.all(data.positions.map((p) => deps.d365.getPosition(p.positionId)));
    const positionIdsElsewhere = new Set(
      positionChecks
        .filter((p) => p && (mode === 'newVenue' || p.companyId !== companyId))
        .map((p) => p?.positionId ?? ''),
    );
    const companies = await deps.d365.listVenueCompanies();
    const workerLists = await Promise.all(companies.map((c) => deps.d365.getWorkers(c.id)));
    const personnelNumbersElsewhere = new Set<string>();
    const existingPersonnelNumbers = new Set<string>();
    const existingUserUpns = new Set<string>();
    companies.forEach((c, i) => {
      for (const w of workerLists[i] ?? []) {
        if (c.id === companyId && mode === 'existingVenue') {
          existingPersonnelNumbers.add(w.personnelNumber);
          if (w.upn) existingUserUpns.add(w.upn);
        } else personnelNumbersElsewhere.add(w.personnelNumber);
      }
    });

    const fileJobs = new Set(data.approvalLimits.map((l) => l.jobId));
    const limitSource = mode === 'existingVenue' ? companyId : tpl.templateLegalEntityId;
    const baseLimits = (await deps.d365.getApprovalLimits(limitSource)).filter(
      (l) => mode === 'existingVenue' || !fileJobs.has(l.jobId),
    );

    const existingRoleHolders: Array<{ roleId: string; name: string; upn: string }> = [];
    if (await deps.d365.getLegalEntity(companyId)) {
      const assignments = await deps.d365.listUserRoleAssignments(companyId);
      const users = await Promise.all(
        [...new Set(assignments.map((a) => a.userId))].map((u) => deps.d365.getUser(u)),
      );
      for (const a of assignments) {
        const u = users.find((x) => x?.userId === a.userId);
        if (u) existingRoleHolders.push({ roleId: a.roleId, name: u.name, upn: u.upn });
      }
    }

    return {
      mode,
      companyId,
      template: tpl,
      answers: req ? answersOf(req.id) : null,
      directory,
      existingPositions,
      positionIdsElsewhere,
      personnelNumbersElsewhere,
      existingUserUpns,
      existingPersonnelNumbers,
      baseLimits,
      existingRoleHolders,
    };
  }

  function rawRows(importId: string): RawPeopleRow[] {
    return rowsOf(importId).map((r) => ({ id: r.id, sheet: r.sheet, rowNumber: r.rowNumber, data: r.data }));
  }

  /** Template groups and accounts for the master data rules (P9–P11). */
  async function masterDataContext(imp: PeopleImportRecord, data: PeopleData): Promise<MasterDataContext> {
    const req = imp.requestId ? getRequestRecord(imp.requestId) : null;
    const templateCompanyId = template(req?.templateId ?? 'LTPL').templateLegalEntityId;
    const empty = new Set<string>();
    const hasMasterData = MASTER_DATA_SHEETS.some((s) => data[s].length > 0);
    if (!hasMasterData)
      return {
        templateCompanyId,
        groups: null,
        templateCustomers: empty,
        templateVendors: empty,
        existingPersonnelNumbers: empty,
      };
    const [groups, customers, vendors] = await Promise.all([
      deps.d365.listMasterDataGroups(templateCompanyId).catch(() => null),
      deps.d365.listCustomers(templateCompanyId).catch(() => []),
      deps.d365.listVendors(templateCompanyId).catch(() => []),
    ]);
    const known =
      groups && groups.customerGroups.length + groups.vendorGroups.length + groups.projectGroups.length > 0;
    const existingPersonnelNumbers = imp.requestId
      ? empty
      : new Set((await deps.d365.getWorkers(importCompanyId(imp))).map((w) => w.personnelNumber));
    return {
      templateCompanyId,
      groups: known
        ? {
            customerGroups: new Set(groups.customerGroups),
            vendorGroups: new Set(groups.vendorGroups),
            projectGroups: new Set(groups.projectGroups),
          }
        : null,
      templateCustomers: new Set(customers.map((c) => c.customerAccount)),
      templateVendors: new Set(vendors.map((v) => v.vendorId)),
      existingPersonnelNumbers,
    };
  }

  async function runValidation(imp: PeopleImportRecord, actor: string): Promise<void> {
    const rows = rawRows(imp.id);
    const ctx = await peopleContext(imp, rows);
    const parsed = parsePeopleRows(rows);
    const result = validatePeople(parsed.data, ctx);
    const masterIssues = validateMasterData(parsed.data, await masterDataContext(imp, parsed.data));
    const all = [...parsed.issues, ...result.issues, ...masterIssues];
    const rowKey = (sheet: PeopleSheet | null, n: number | null): string => `${sheet}|${n}`;
    const byRow = new Map<string, PeopleIssue[]>();
    const importIssues: PeopleIssue[] = [];
    for (const issue of all) {
      if (issue.sheet && issue.rowNumber !== null) {
        const k = rowKey(issue.sheet, issue.rowNumber);
        byRow.set(k, [...(byRow.get(k) ?? []), issue]);
      } else importIssues.push(issue);
    }
    for (const r of rowsOf(imp.id)) r.issues = byRow.get(rowKey(r.sheet, r.rowNumber)) ?? [];
    imp.importIssues = importIssues;
    imp.coverage = result.coverage;
    imp.status = all.some((i) => i.severity === 'error') ? 'Invalid' : 'Valid';
    imp.validatedAt = stamp();
    audit(actor, 'people.validated', imp.requestId, {
      importId: imp.id,
      status: imp.status,
      errors: all.filter((i) => i.severity === 'error').length,
      warnings: all.filter((i) => i.severity === 'warning').length,
    });
  }

  function importView(user: CurrentUser, imp: PeopleImportRecord): PeopleImportView {
    const rows = rowsOf(imp.id).sort((a, b) => a.sheet.localeCompare(b.sheet) || a.rowNumber - b.rowNumber);
    const issues = [...imp.importIssues, ...rows.flatMap((r) => r.issues)];
    const counts = Object.fromEntries(
      SHEETS.map((s) => {
        const sheetRows = rows.filter((r) => r.sheet === s.sheet);
        const sheetIssues = issues.filter((i) => i.sheet === s.sheet);
        return [
          s.sheet,
          {
            rows: sheetRows.length,
            errors: sheetIssues.filter((i) => i.severity === 'error').length,
            warnings: sheetIssues.filter((i) => i.severity === 'warning').length,
          },
        ];
      }),
    ) as PeopleImportView['counts'];
    const { data } = parsePeopleRows(rawRows(imp.id));
    const submitters = data.assignments
      .filter((a) => a.primary)
      .flatMap((a) => {
        const w = data.workers.find((x) => x.personnelNumber === a.personnelNumber);
        return w ? [{ upn: w.upn, name: `${w.firstName} ${w.lastName}`, positionId: a.positionId }] : [];
      });
    return {
      import: imp,
      rows,
      issues,
      coverage: imp.coverage,
      counts,
      submitters,
      companyId: importCompanyId(imp),
      canEdit: canEditImport(user, imp),
    };
  }

  function addRows(
    importId: string,
    rows: Array<{ sheet: PeopleSheet; data: Record<string, unknown>; rowNumber?: number }>,
  ): void {
    const next = new Map<PeopleSheet, number>();
    for (const r of rowsOf(importId)) next.set(r.sheet, Math.max(next.get(r.sheet) ?? 1, r.rowNumber));
    for (const r of rows) {
      const rowNumber = r.rowNumber ?? (next.get(r.sheet) ?? 1) + 1;
      next.set(r.sheet, Math.max(next.get(r.sheet) ?? 1, rowNumber));
      db().peopleRows.push({
        id: newId(),
        importId,
        sheet: r.sheet,
        rowNumber,
        data: normaliseRowData(r.sheet, r.data),
        issues: [],
      });
    }
  }

  // ── lifecycle and provisioning internals ───────────────────────────────────

  /** Demo: only "does it already exist" (D365 or another open request); no ID pattern rule. */
  async function idAvailability(rawId: string | null, requestId: string | null): Promise<IdAvailability> {
    if (!rawId) return { available: false, reason: 'No legal entity ID entered' };
    const id = rawId.trim().toUpperCase();
    const other = db().requests.find(
      (r) => r.legalEntityId === id && r.status !== 'Cancelled' && r.id !== requestId,
    );
    if (other) return { available: false, reason: `Used by request "${other.venueName}"` };
    if (!(await deps.d365.isLegalEntityIdAvailable(id)))
      return { available: false, reason: `${id} already exists in D365` };
    return { available: true };
  }

  async function gate1For(req: RequestRecord): Promise<GateResult> {
    const errors = validateAnswers(answersOf(req.id), questionContext(req));
    const imp = latestImportFor(req.id);
    return evaluateGate1({
      answerErrorCount: Object.keys(errors).length,
      idAvailability: ['Draft', 'Submitted', 'InReview', 'Approved'].includes(req.status)
        ? await idAvailability(req.legalEntityId, req.id)
        : { available: true },
      peopleStatus: imp?.status ?? null,
    });
  }

  function dryRunFresh(req: RequestRecord): boolean {
    const t = template(req.templateId);
    return !!t.lastDryRunAt && (!t.lastTemplateChangeAt || t.lastDryRunAt > t.lastTemplateChangeAt);
  }

  function applyTransition(
    req: RequestRecord,
    action: LifecycleAction,
    actor: CurrentUser | 'system',
    ctx: LifecycleContext = {},
    payload: Record<string, unknown> = {},
  ): void {
    const r = transition(req, action, actor, ctx);
    if (!r.next) throw new ServiceError(409, 'Not allowed', r.errors.join('; '));
    const from = req.status;
    req.status = r.next.status;
    req.currentGate = r.next.currentGate;
    req.updatedAt = stamp();
    audit(actor === 'system' ? 'system' : actor.upn, `request.${action.type}`, req.id, {
      from,
      to: req.status,
      version: req.version,
      ...payload,
    });
  }

  function tasksOf(requestId: string): TaskRecord[] {
    return db().tasks.filter((t) => t.requestId === requestId);
  }

  function addTask(
    req: RequestRecord,
    owner: string,
    task: Omit<TaskRecord, 'id' | 'requestId' | 'status'>,
  ): void {
    if (tasksOf(req.id).some((t) => t.taskKey === task.taskKey)) return;
    db().tasks.push({
      id: newId(),
      requestId: req.id,
      status: 'Open',
      taskKey: task.taskKey,
      description: task.description,
      ownerUpn: task.ownerUpn ?? owner,
      dueDate: task.dueDate ?? req.targetGoLive,
    });
    audit('system', 'task.created', req.id, { taskKey: task.taskKey });
  }

  function runView(runId: string): RunView {
    const run = db().runs.find((r) => r.id === runId);
    if (!run) throw new ServiceError(404, 'Not found', 'Run not found');
    const { answersSnapshot: _a, peopleSnapshot: _p, ...rest } = run;
    return {
      run: rest,
      steps: db()
        .steps.filter((s) => s.runId === runId)
        .sort((a, b) => a.sequence - b.sequence),
      tasks: run.requestId ? tasksOf(run.requestId) : [],
      liveSteps: [...liveSteps],
    };
  }

  async function driveRun(runId: string): Promise<void> {
    if (driving.has(runId)) return;
    driving.add(runId);
    try {
      for (;;) {
        const run = db().runs.find((r) => r.id === runId);
        if (!run || run.status !== 'Running') return;
        const req = run.requestId ? getRequestRecord(run.requestId) : null;
        const steps = db().steps.filter((s) => s.runId === runId);
        const next = planNext(steps, PROVISION_PLAN);
        if (next === 'blocked') return;
        if (next === 'done') {
          run.status = 'Succeeded';
          run.endedAt = stamp();
          if (req) applyTransition(req, { type: 'stepsDone' }, 'system', {}, { runId });
          audit('system', 'run.completed', run.requestId, { runId });
          commit();
          return;
        }
        const step = steps.find((s) => s.stepKey === next);
        if (!step) return;
        step.status = 'Running';
        step.attempt += 1;
        step.startedAt = stamp();
        step.error = null;
        commit();

        const env: ActivityEnv = {
          d365: deps.d365,
          run,
          companyId: req?.legalEntityId ?? '',
          template: template(req?.templateId ?? 'LTPL'),
          flags,
          pollMs,
          timeoutMs,
          now: () => now().getTime(),
          sleep,
          progress: (text, executionIds) => {
            step.output = { ...(step.output ?? {}), progress: text };
            if (executionIds?.length) step.d365ExecutionId = executionIds.join(',');
            commit();
          },
          addTask: (task) => {
            if (req) addTask(req, run.startedBy, task);
          },
          tasks: () => (req ? tasksOf(req.id) : []),
          profile,
          stepOutput: (key) =>
            db().steps.find((s) => s.runId === run.id && s.stepKey === key)?.output ?? null,
          liveSteps,
          otherActiveCopy: () =>
            db().runs.some(
              (r) =>
                r.id !== run.id &&
                r.status === 'Running' &&
                db().steps.some(
                  (s) => s.runId === r.id && s.stepKey === 'copyFromTemplate' && s.status === 'Running',
                ),
            ),
        };
        try {
          const result = await ACTIVITIES[next](env);
          step.status = result.status;
          step.output = result.output;
          if (result.executionIds?.length) step.d365ExecutionId = result.executionIds.join(',');
          step.endedAt = stamp();
          if (next === 'importPeople' && run.peopleImportId) {
            const imp = db().peopleImports.find((i) => i.id === run.peopleImportId);
            if (imp) {
              imp.status = 'Applied';
              imp.d365ExecutionId = step.d365ExecutionId;
            }
          }
          audit('system', 'run.stepFinished', run.requestId, { runId, stepKey: next, status: result.status });
          commit();
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Step failed';
          step.status = 'Failed';
          step.error = message;
          step.endedAt = stamp();
          run.status = 'NeedsAttention';
          if (req) applyTransition(req, { type: 'stepFailed' }, 'system', {}, { runId, stepKey: next });
          audit('system', 'run.stepFailed', run.requestId, { runId, stepKey: next, error: message });
          commit();
          return;
        }
      }
    } finally {
      driving.delete(runId);
    }
  }

  // ── readiness testing (build update 2.4) ────────────────────────────────────

  /**
   * People context for a venue with no import in the app (the seeded live venues):
   * the regional chain above its positions, template limits and the regional finance
   * role holder, all from mock data. Never calls a live D365 method.
   */
  async function seedPeopleContext(
    companyId: string,
    data: PeopleData,
    tpl: TemplateConfig,
  ): Promise<PeopleContext> {
    const inFile = new Set(data.positions.map((p) => p.positionId));
    const existingPositions = new Map<string, ExistingPosition>();
    let frontier = [...new Set(data.positions.map((p) => p.reportsToPositionId))].filter(
      (id) => id && !inFile.has(id),
    );
    for (let depth = 0; frontier.length && depth < 10; depth++) {
      const next: string[] = [];
      for (const p of await Promise.all(frontier.map((id) => deps.d365.getPosition(id)))) {
        if (!p || existingPositions.has(p.positionId)) continue;
        existingPositions.set(p.positionId, {
          positionId: p.positionId,
          title: p.title,
          jobId: p.jobId,
          companyId: p.companyId,
          reportsToPositionId: p.reportsToPositionId,
          holder:
            p.holderPersonnelNumber && p.holderName
              ? { name: p.holderName, upn: p.holderUpn, personnelNumber: p.holderPersonnelNumber }
              : null,
        });
        if (p.reportsToPositionId) next.push(p.reportsToPositionId);
      }
      frontier = next;
    }
    const fileJobs = new Set(data.approvalLimits.map((l) => l.jobId));
    const regional = MOCK_PEOPLE.find((p) => p.placement === 'corp:REG-FIN-01');
    return {
      mode: 'newVenue',
      companyId,
      template: tpl,
      answers: null,
      directory: new Map(),
      existingPositions,
      positionIdsElsewhere: new Set(),
      personnelNumbersElsewhere: new Set(),
      existingUserUpns: new Set(),
      existingPersonnelNumbers: new Set(),
      baseLimits: (await deps.d365.getApprovalLimits(tpl.templateLegalEntityId)).filter(
        (l) => !fileJobs.has(l.jobId),
      ),
      existingRoleHolders: regional
        ? [
            {
              roleId: 'LEDGERFINANCIALCONTROLLER',
              name: `${regional.firstName} ${regional.lastName}`,
              upn: regional.upn,
            },
          ]
        : [],
    };
  }

  /** Everything the plan generator needs, or why it can't run yet. */
  async function readinessInput(
    companyId: string,
    req: RequestRecord | null,
    includeDemo: boolean,
  ): Promise<PlanInput | string> {
    const tpl = template(req?.templateId ?? 'LTPL');
    const ctx = contextFor(tpl.id);
    const answers = withDefaults(req ? answersOf(req.id) : {}, ctx);
    const imp = req ? latestImportFor(req.id) : null;
    let people: PeopleData;
    let pctx: PeopleContext;
    if (imp && rowsOf(imp.id).length) {
      const rows = rawRows(imp.id);
      people = parsePeopleRows(rows).data;
      pctx = await peopleContext(imp, rows);
    } else {
      const seeded = seededVenuePeopleRows(companyId, now());
      if (!seeded) return 'Load the people and approvals first: the plan is built from them.';
      people = parsePeopleRows(
        seeded.map((r, i) => ({ id: `seed-${i}`, sheet: r.sheet, rowNumber: i + 2, data: r.data })),
      ).data;
      pctx = await seedPeopleContext(companyId, people, tpl);
    }
    const devs = deviations(answers, ctx);
    const text = (k: string): string => {
      const v = answers[k];
      return typeof v === 'boolean' ? (v ? 'Yes' : 'No') : v === undefined || v === null ? '' : String(v);
    };
    const governed: GovernedSetting[] = [
      { key: 'currency', label: 'Accounting currency', q: 'finance.currency', tv: ctx.defaults.currency },
      {
        key: 'fiscalCalendar',
        label: 'Fiscal calendar',
        q: 'finance.fiscalCalendar',
        tv: ctx.defaults.fiscalCalendar,
      },
      {
        key: 'chartOfAccounts',
        label: 'Chart of accounts',
        q: 'finance.chartOfAccounts',
        tv: ctx.defaults.chartOfAccounts,
      },
      {
        key: 'matchingPolicy',
        label: 'Invoice matching policy',
        q: 'procurement.matchingPolicy',
        tv: ctx.defaults.matchingPolicy,
      },
      {
        key: 'numberSequences',
        label: 'Number sequences copied',
        q: 'finance.copyNumberSequences',
        tv: ctx.defaults.copyNumberSequences ? 'Yes' : 'No',
      },
      // Unique per venue, so there is no template value to match; the company ID stands in if unanswered.
      {
        key: 'venueDimension',
        label: 'Venue dimension value',
        q: 'finance.venueDimensionValue',
        tv: 'unique per venue',
      },
    ].map((g) => ({
      key: g.key,
      label: g.label,
      value: text(g.q) || (g.key === 'venueDimension' ? companyId : g.tv),
      templateValue: g.tv,
      deviation: devs.includes(g.q),
    }));
    const modules = Array.isArray(answers['venue.modules'])
      ? (answers['venue.modules'] as unknown[]).map(String)
      : ['Procurement', 'AP', 'Projects'];
    return {
      requestId: req?.id ?? null,
      company: companyId,
      generatedAt: stamp(),
      templateVersion: `${tpl.id}@${(tpl.lastTemplateChangeAt ?? stamp()).slice(0, 10)}`,
      modules,
      people,
      routingPeople: buildRoutingPeople(people, pctx),
      routing: tpl.routing,
      roleCatalog: tpl.roleCatalog,
      governed,
      copiedEntitySets: COPIED_ENTITY_SETS,
      otherCompany: companyId === 'V001' ? 'V002' : 'V001',
      overlays: ledgerOverlaysOf(answers),
      ledger: templateLedgerFor(profile.templateCompany),
      templateCompany: tpl.templateLegalEntityId,
      includeDemo,
    };
  }

  async function createReadinessPlan(
    companyId: string,
    req: RequestRecord | null,
    environment: ReadinessEnv,
    actor: string,
    includeDemo = false,
  ): Promise<ReadinessPlanRecord> {
    const input = await readinessInput(companyId, req, includeDemo);
    if (typeof input === 'string') throw new ServiceError(400, 'Plan not available yet', input);
    const plan = generateReadinessPlan(input);
    if (environment === 'PROD') plan.cases = plan.cases.filter((c) => c.envs.includes('PROD'));
    const rec: ReadinessPlanRecord = {
      id: newId(),
      requestId: req?.id ?? null,
      companyId,
      environment,
      plan,
      templateVersion: plan.templateVersion,
      createdBy: actor,
      createdAt: stamp(),
    };
    db().readinessPlans.push(rec);
    audit(actor, 'readiness.planGenerated', req?.id ?? null, {
      planId: rec.id,
      environment,
      cases: plan.cases.length,
    });
    return rec;
  }

  const hashOf = (s: string): number => {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
    return h >>> 0;
  };

  /** Simulated run time per case, by layer (UI cases take longer); stable per case. */
  const mockDuration = (c: ReadinessCase): number => {
    const rng = createRng(hashOf(c.id));
    if (c.layer === 'L1' || c.layer === 'L4') return rng.int(400, 1800);
    if (c.layer === 'L2') return rng.int(6000, 18000);
    return rng.int(20000, 75000);
  };

  type ForcedFailure = { kind: FailureKind; summary: string };

  /**
   * In-browser stand-in for the Playwright runner (R2 runs the real one): every case
   * passes unless it is the seeded demo failure, its routing has a gap, a campaign forces
   * a failure, or a case it depends on did not pass. Results arrive one by one.
   */
  async function executeReadinessRun(
    runId: string,
    opts: {
      instant: boolean;
      forced?: ReadonlyMap<string, ForcedFailure>;
      layers?: readonly ReadinessLayer[];
    },
  ): Promise<void> {
    const run = db().readinessRuns.find((r) => r.id === runId);
    const planRec = run ? db().readinessPlans.find((p) => p.id === run.planId) : undefined;
    if (!run || !planRec) return;
    run.status = 'Running';
    run.startedAt ??= stamp();
    commit();
    const outcome = new Map<string, ReadinessResultRecord['status']>();
    for (const r of db().readinessResults.filter((x) => x.runId === runId)) outcome.set(r.caseId, r.status);
    const cases = planRec.plan.cases.filter(
      (c) => (!opts.layers || opts.layers.includes(c.layer)) && !(run.campaignId && c.demo),
    );
    for (const c of cases) {
      if (outcome.has(c.id)) continue;
      const blocker = (c.dependsOn ?? []).find((d) => outcome.has(d) && outcome.get(d) !== 'passed');
      const gap = typeof c.expect['gap'] === 'string' ? (c.expect['gap'] as string) : null;
      const forced = opts.forced?.get(c.id);
      let status: ReadinessResultRecord['status'] = 'passed';
      let errorSummary: string | null = null;
      let failureKind: FailureKind | null = null;
      if (blocker) {
        status = 'skipped';
        errorSummary = `Not run: ${blocker} did not pass`;
      } else if (c.demo) {
        status = 'failed';
        failureKind = 'Behavior';
        errorSummary = `"${String(c.data['forms'])}" refused: "You do not have access to this form." The form is not in the ${String(c.data['role'])} bundle.`;
      } else if (forced) {
        status = 'failed';
        failureKind = forced.kind;
        errorSummary = forced.summary;
      } else if (gap) {
        status = 'failed';
        failureKind = 'Behavior';
        errorSummary = `Routing: ${gap}`;
      }
      outcome.set(c.id, status);
      db().readinessResults.push({
        id: newId(),
        runId,
        caseId: c.id,
        status,
        durationMs: status === 'skipped' ? 0 : mockDuration(c),
        errorSummary,
        failureKind,
        traceUrl: null,
        screenshotUrl: null,
        waivedBy: null,
        waiveReason: null,
        waivedAt: null,
      });
      if (!opts.instant) {
        commit();
        await sleep(250);
      }
    }
    finishReadinessRun(run);
    commit();
  }

  function finishReadinessRun(run: ReadinessRunRecord): void {
    const results = db().readinessResults.filter((r) => r.runId === run.id);
    const failed = results.filter((r) => r.status === 'failed');
    run.status = failed.some((r) => !r.waivedBy) ? 'Failed' : failed.length ? 'Waived' : 'Passed';
    run.endedAt ??= stamp();
  }

  function runSummary(run: ReadinessRunRecord): ReadinessRunSummary {
    const plan = db().readinessPlans.find((p) => p.id === run.planId);
    const results = db().readinessResults.filter((r) => r.runId === run.id);
    const req = plan?.requestId ? db().requests.find((r) => r.id === plan.requestId) : undefined;
    return {
      id: run.id,
      planId: run.planId,
      company: plan?.companyId ?? '',
      venueName: req?.venueName ?? null,
      requestId: plan?.requestId ?? null,
      environment: run.environment,
      status: run.status,
      label: run.label,
      campaignId: run.campaignId,
      d365Version: run.d365Version,
      startedAt: run.startedAt,
      endedAt: run.endedAt,
      total: plan ? plan.plan.cases.filter((c) => !(run.campaignId && c.demo)).length : results.length,
      passed: results.filter((r) => r.status === 'passed').length,
      failed: results.filter((r) => r.status === 'failed' && !r.waivedBy).length,
      skipped: results.filter((r) => r.status === 'skipped').length,
      waived: results.filter((r) => r.status === 'failed' && r.waivedBy).length,
      durationMs: results.reduce((n, r) => n + r.durationMs, 0),
    };
  }

  /**
   * Demo data for section 15: a plan and runs for the seeded live venues, Lakeshore's
   * with the one demo failure, and a 10.0.48 → 10.0.49 campaign with one UI-change and
   * one behavior regression. Timestamps relative to the seed's as-of date.
   */
  async function seedReadiness(asOf: Date): Promise<void> {
    const day = 24 * 60 * 60 * 1000;
    const iso = (offsetMs: number): string => new Date(asOf.getTime() + offsetMs).toISOString();
    const plans = new Map<string, ReadinessPlanRecord>();
    for (const companyId of ['V001', 'V002', 'V003']) {
      const req = db().requests.find((r) => r.legalEntityId === companyId) ?? null;
      const plan = await createReadinessPlan(companyId, req, 'TEST', 'system', companyId === 'V003');
      plan.createdAt = iso(-30 * day);
      plan.plan.generatedAt = plan.createdAt;
      plans.set(companyId, plan);
    }
    const addRun = async (
      companyId: string,
      startOffset: number,
      extra: Partial<ReadinessRunRecord>,
      opts: { forced?: ReadonlyMap<string, ForcedFailure>; layers?: readonly ReadinessLayer[] } = {},
    ): Promise<ReadinessRunRecord> => {
      const plan = plans.get(companyId);
      if (!plan) throw new Error(`No seeded plan for ${companyId}`);
      const run: ReadinessRunRecord = {
        id: newId(),
        planId: plan.id,
        campaignId: null,
        label: null,
        environment: 'TEST',
        status: 'Queued',
        d365Version: '10.0.48',
        startedBy: 'system',
        startedAt: iso(startOffset),
        endedAt: null,
        reportUrl: null,
        runnerRef: 'mock',
        ...extra,
      };
      db().readinessRuns.push(run);
      await executeReadinessRun(run.id, { instant: true, ...opts });
      const total = db()
        .readinessResults.filter((r) => r.runId === run.id)
        .reduce((n, r) => n + r.durationMs, 0);
      // Personas run in parallel (section 8): wall time is roughly a quarter of the test time.
      run.endedAt = iso(startOffset + Math.round(total / 4));
      return run;
    };

    // Readiness runs after provisioning: V001 and V002 passed; Lakeshore has the demo failure.
    await addRun('V001', -60 * day, {});
    await addRun('V002', -45 * day, {});
    await addRun('V003', -26 * day, {});

    // Upgrade campaign: L1–L4 on one venue per archetype (V001 arena, V002 stadium), L1 and L4 on V003.
    const campaign: UpgradeCampaignRecord = {
      id: newId(),
      name: '10.0.49 update in TEST',
      fromVersion: '10.0.48',
      toVersion: '10.0.49',
      baselineRunIds: [],
      candidateRunIds: [],
      status: 'Compared',
      createdAt: iso(-12 * day),
    };
    db().campaigns.push(campaign);
    const scope: Array<[string, readonly ReadinessLayer[] | undefined]> = [
      ['V001', undefined],
      ['V002', undefined],
      ['V003', ['L1', 'L4']],
    ];
    for (const [companyId, layers] of scope) {
      const run = await addRun(
        companyId,
        -12 * day,
        { campaignId: campaign.id, label: 'Baseline · 10.0.48', d365Version: '10.0.48' },
        { layers },
      );
      campaign.baselineRunIds.push(run.id);
    }
    const forced = new Map<string, ForcedFailure>([
      [
        'V001-P2P-01-BUY-4000',
        {
          kind: 'UiChange',
          summary:
            'Control "Vendor account" (PurchCreateOrder_OrderAccount) not found on the Create purchase order dialog. Candidate for a selector fix.',
        },
      ],
      [
        'V002-ACC-02-VENDPURCHASINGAGENT',
        {
          kind: 'Behavior',
          summary:
            'Cross-company URL ?cmp=V001 switched the session to V002 instead of refusing it. Expected a refusal message.',
        },
      ],
    ]);
    for (const [companyId, layers] of scope) {
      const run = await addRun(
        companyId,
        -5 * day,
        { campaignId: campaign.id, label: 'Candidate · 10.0.49', d365Version: '10.0.49' },
        { layers, forced },
      );
      campaign.candidateRunIds.push(run.id);
    }
  }

  // ── public API ─────────────────────────────────────────────────────────────

  const service = {
    /** Create the demo dataset on first run (or after a reset). */
    async ensureSeeded(): Promise<void> {
      if (data ?? deps.store.load()) {
        db();
        // Pick up runs interrupted by a page reload (Durable Functions does this in Azure).
        for (const run of db().runs.filter((r) => r.status === 'Running')) schedule(() => driveRun(run.id));
        for (const run of db().readinessRuns.filter((r) => r.status === 'Running' || r.status === 'Queued'))
          schedule(() => executeReadinessRun(run.id, { instant: false }));
        return;
      }
      const asOf = now();
      data = {
        version: 1,
        seededAsOf: asOf.toISOString(),
        templates: seedTemplateConfigs(asOf),
        requests: [],
        answers: [],
        approvals: [],
        peopleImports: [],
        peopleRows: [],
        runs: [],
        steps: [],
        tasks: [],
        audit: [],
        readinessPlans: [],
        readinessRuns: [],
        readinessResults: [],
        campaigns: [],
      };
      for (const seed of demoRequests(asOf)) {
        const id = newId();
        const req: RequestRecord = {
          id,
          legalEntityId: seed.legalEntityId,
          venueName: seed.venueName,
          templateId: 'LTPL',
          status: seed.status,
          currentGate: seed.currentGate,
          requesterUpn: seed.requesterUpn,
          targetGoLive: seed.targetGoLive,
          version: 1,
          createdAt: asOf.toISOString(),
          updatedAt: asOf.toISOString(),
        };
        data.requests.push(req);
        const ctx = questionContext(req);
        const answers = withDefaults(seed.answers, ctx);
        for (const [questionKey, value] of Object.entries(answers)) {
          data.answers.push({ requestId: id, questionKey, value, validatedAt: null });
        }
        audit('system', 'request.seeded', id, { status: seed.status });
        if (seed.peopleImport) {
          const imp: PeopleImportRecord = {
            id: newId(),
            requestId: id,
            companyId: null,
            source: 'File',
            sourceCompanyId: null,
            status: 'Draft',
            uploadedBy: seed.requesterUpn,
            uploadedAt: asOf.toISOString(),
            validatedAt: null,
            d365ExecutionId: null,
            importIssues: [],
            coverage: null,
          };
          data.peopleImports.push(imp);
          addRows(imp.id, seed.peopleImport.rows);
          await runValidation(imp, 'system');
        }
      }
      await seedReadiness(asOf);
      commit();
    },

    async listRequests(user: CurrentUser): Promise<RequestSummary[]> {
      requireAnyRole(user);
      return db()
        .requests.map((r) => {
          const imp = latestImportFor(r.id);
          return {
            id: r.id,
            legalEntityId: r.legalEntityId,
            venueName: r.venueName,
            status: r.status,
            currentGate: r.currentGate,
            requesterUpn: r.requesterUpn,
            targetGoLive: r.targetGoLive,
            updatedAt: r.updatedAt,
            peopleStatus: imp?.status ?? null,
          };
        })
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    },

    async createRequest(
      user: CurrentUser,
      input: { venueName: string; templateId: string },
    ): Promise<RequestDetail> {
      requireRole(user, ['Requester']);
      const venueName = input.venueName.trim();
      if (!venueName) throw new ServiceError(400, 'Validation failed', 'Venue name is required');
      const tpl = template(input.templateId);
      const req: RequestRecord = {
        id: newId(),
        legalEntityId: null,
        venueName,
        templateId: tpl.id,
        status: 'Draft',
        currentGate: 1,
        requesterUpn: user.upn,
        targetGoLive: null,
        version: 1,
        createdAt: stamp(),
        updatedAt: stamp(),
      };
      db().requests.push(req);
      const answers = withDefaults(
        { 'venue.name': venueName, 'finance.templateId': tpl.id },
        questionContext(req),
      );
      for (const [questionKey, value] of Object.entries(answers)) {
        db().answers.push({ requestId: req.id, questionKey, value, validatedAt: null });
      }
      audit(user.upn, 'request.created', req.id, { venueName, templateId: tpl.id });
      commit();
      return service.getRequest(user, req.id);
    },

    async getRequest(user: CurrentUser, id: string): Promise<RequestDetail> {
      requireAnyRole(user);
      const req = getRequestRecord(id);
      const ctx = questionContext(req);
      const answers = answersOf(id);
      const errors = validateAnswers(answers, ctx);
      const imp = latestImportFor(id);
      return {
        request: { ...req },
        answers,
        errors,
        errorCountBySection: {
          ...errorCountBySection(errors),
          people: imp ? (imp.status === 'Valid' ? 0 : Math.max(1, importSummary(imp).errorCount)) : 1,
        },
        deviations: deviations(answers, ctx),
        deviationDetails: deviationDetails(answers, ctx),
        template: ctx.template,
        defaults: ctx.defaults,
        today: ctx.today,
        goLiveLeadDays: leadDays,
        peopleImport: imp ? importSummary(imp) : null,
        canEdit: canEditRequest(user, req),
        approvals: db()
          .approvals.filter((x) => x.requestId === id)
          .sort((x, y) => x.decidedAt.localeCompare(y.decidedAt)),
        gate1: await gate1For(req),
        dryRunFresh: dryRunFresh(req),
      };
    },

    async saveAnswers(user: CurrentUser, id: string, patch: Answers): Promise<RequestDetail> {
      const req = getRequestRecord(id);
      if (!canEditRequest(user, req))
        throw new ServiceError(
          403,
          'Forbidden',
          'Only the requester can edit this request before provisioning',
        );
      const ctx = questionContext(req);
      const previousId = req.legalEntityId;
      let changed = false;
      for (const [key, raw] of Object.entries(patch)) {
        const q = QUESTION_BY_KEY.get(key);
        if (!q) throw new ServiceError(400, 'Validation failed', `Unknown question ${key}`);
        // Overlay rows are stored trimmed, without blank rows.
        let value = typeof raw === 'string' ? raw.trim() : comparableAnswer(q, raw);
        if (key === 'entity.id' && typeof value === 'string') value = value.toUpperCase();
        if (q.sensitive && typeof value === 'string' && value) {
          const problems = q.validate?.(value, {}, ctx) ?? [];
          if (problems.length)
            throw new ServiceError(400, 'Validation failed', `${q.label}: ${problems.join('; ')}`);
          // Browser/document store keeps only the masked form. The Azure API
          // encrypts the full value instead (Phase 2, spec 14).
          value = maskEin(value);
        }
        const existing = db().answers.find((a) => a.requestId === id && a.questionKey === key);
        if (existing && JSON.stringify(existing.value) === JSON.stringify(value)) continue;
        changed = true;
        if (existing) existing.value = value;
        else
          db().answers.push({
            requestId: id,
            questionKey: key,
            value,
            validatedAt: null,
          } satisfies AnswerRecord);
        if (key === 'entity.id') req.legalEntityId = typeof value === 'string' && value ? value : null;
        if (key === 'venue.name' && typeof value === 'string' && value) req.venueName = value;
        if (key === 'venue.targetGoLive')
          req.targetGoLive = typeof value === 'string' && value ? value : null;
        if (key === 'finance.templateId' && typeof value === 'string') req.templateId = value;
      }
      // The venue dimension value defaults to the legal entity ID: filled while it is empty
      // or still the previous ID, never over a value someone typed.
      if (
        req.legalEntityId &&
        req.legalEntityId !== previousId &&
        !('finance.venueDimensionValue' in patch)
      ) {
        const dim = db().answers.find(
          (a) => a.requestId === id && a.questionKey === 'finance.venueDimensionValue',
        );
        const current = typeof dim?.value === 'string' ? dim.value : '';
        if (!current || current === previousId) {
          if (dim) dim.value = req.legalEntityId;
          else
            db().answers.push({
              requestId: id,
              questionKey: 'finance.venueDimensionValue',
              value: req.legalEntityId,
              validatedAt: null,
            });
          changed = true;
        }
      }
      if (changed) {
        markEdited(req, user.upn);
        audit(user.upn, 'request.answersSaved', id, {
          keys: Object.keys(patch).map((k) => (QUESTION_BY_KEY.get(k)?.sensitive ? `${k} (masked)` : k)),
        });
        commit();
      }
      return service.getRequest(user, id);
    },

    async checkLegalEntityId(user: CurrentUser, rawId: string, requestId?: string): Promise<IdAvailability> {
      requireAnyRole(user);
      if (requestId) getRequestRecord(requestId); // 404 for an unknown request
      return idAvailability(rawId, requestId ?? null);
    },

    // ── people and approvals ────────────────────────────────────────────────

    async getPeopleImport(user: CurrentUser, importId: string): Promise<PeopleImportView> {
      requireAnyRole(user);
      return importView(user, requireImport(importId));
    },

    async createPeopleImport(
      user: CurrentUser,
      requestId: string,
      source: PeopleImportSourceInput,
    ): Promise<PeopleImportView> {
      const req = getRequestRecord(requestId);
      if (!canEditRequest(user, req))
        throw new ServiceError(403, 'Forbidden', 'Only the requester can change the people import');
      if (!req.legalEntityId)
        throw new ServiceError(
          400,
          'Legal entity ID needed',
          'Enter the legal entity ID first; position IDs are based on it.',
        );
      const companyId = req.legalEntityId;
      const imp: PeopleImportRecord = {
        id: newId(),
        requestId,
        companyId: null,
        source: source.kind === 'copy' ? 'CopiedFromVenue' : source.kind === 'empty' ? 'Grid' : 'File',
        sourceCompanyId: source.kind === 'copy' ? source.companyId : null,
        status: 'Draft',
        uploadedBy: user.upn,
        uploadedAt: stamp(),
        validatedAt: null,
        d365ExecutionId: null,
        importIssues: [],
        coverage: null,
      };
      db().peopleImports.push(imp);
      if (source.kind === 'sample') addRows(imp.id, samplePeopleRows(source.variant, companyId, now()));
      if (source.kind === 'rows') addRows(imp.id, source.rows);
      if (source.kind === 'copy') {
        const from = source.companyId.toUpperCase();
        const [positions, limits] = await Promise.all([
          deps.d365.getPositions(from),
          deps.d365.getApprovalLimits(from),
        ]);
        const rename = (pid: string): string =>
          pid.startsWith(`${from}-`) ? `${companyId}-${pid.slice(from.length + 1)}` : pid;
        addRows(imp.id, [
          ...positions.map((p) => ({
            sheet: 'positions' as const,
            data: {
              positionId: rename(p.positionId),
              title: p.title,
              jobId: p.jobId,
              departmentId: p.departmentId ? rename(p.departmentId) : '',
              reportsToPositionId: p.reportsToPositionId ? rename(p.reportsToPositionId) : '',
            },
          })),
          ...limits
            .filter((l) => positions.some((p) => p.jobId === l.jobId))
            .map((l) => ({
              sheet: 'approvalLimits' as const,
              data: { jobId: l.jobId, documentType: l.documentType, amountUsd: l.amountUsd },
            })),
        ]);
      }
      markEdited(req, user.upn);
      audit(user.upn, 'people.imported', requestId, {
        importId: imp.id,
        source: source.kind,
        ...(source.kind === 'sample' ? { variant: source.variant } : {}),
      });
      await runValidation(imp, user.upn);
      commit();
      return importView(user, imp);
    },

    async uploadPeopleFiles(
      user: CurrentUser,
      requestId: string,
      files: Array<{ name: string; bytes: Uint8Array }>,
    ): Promise<{ view: PeopleImportView | null; fileErrors: string[] }> {
      const rows: UploadedRow[] = [];
      const fileErrors: string[] = [];
      for (const f of files) {
        const lower = f.name.toLowerCase();
        if (lower.endsWith('.xlsm')) {
          fileErrors.push(`${f.name}: macro-enabled workbooks are not accepted`);
          continue;
        }
        if (f.bytes.byteLength > 5 * 1024 * 1024) {
          fileErrors.push(`${f.name}: larger than 5 MB`);
          continue;
        }
        const result = lower.endsWith('.csv')
          ? parsePeopleCsv(f.name, new TextDecoder().decode(f.bytes))
          : lower.endsWith('.xlsx')
            ? await parsePeopleWorkbook(f.bytes)
            : { rows: [], fileErrors: [`${f.name}: upload .xlsx or .csv files`] };
        rows.push(...result.rows);
        fileErrors.push(...result.fileErrors);
      }
      if (rows.length === 0)
        return { view: null, fileErrors: fileErrors.length ? fileErrors : ['No rows found'] };
      const view = await service.createPeopleImport(user, requestId, { kind: 'rows', rows });
      return { view, fileErrors };
    },

    async savePeopleRow(
      user: CurrentUser,
      importId: string,
      input: { rowId?: string; sheet: PeopleSheet; data: Record<string, unknown> },
    ): Promise<PeopleImportView> {
      const imp = requireImport(importId);
      if (!canEditImport(user, imp)) throw new ServiceError(403, 'Forbidden', 'You cannot edit this import');
      if (input.rowId) {
        const row = rowsOf(importId).find((r) => r.id === input.rowId);
        if (!row) throw new ServiceError(404, 'Not found', 'Row not found');
        row.data = normaliseRowData(row.sheet, input.data);
      } else {
        addRows(importId, [{ sheet: input.sheet, data: input.data }]);
      }
      if (imp.requestId) markEdited(getRequestRecord(imp.requestId), user.upn);
      audit(user.upn, input.rowId ? 'people.rowEdited' : 'people.rowAdded', imp.requestId, {
        importId,
        sheet: input.sheet,
      });
      await runValidation(imp, user.upn);
      commit();
      return importView(user, imp);
    },

    async deletePeopleRow(user: CurrentUser, importId: string, rowId: string): Promise<PeopleImportView> {
      const imp = requireImport(importId);
      if (!canEditImport(user, imp)) throw new ServiceError(403, 'Forbidden', 'You cannot edit this import');
      const d = db();
      const row = d.peopleRows.find((r) => r.id === rowId && r.importId === importId);
      if (!row) throw new ServiceError(404, 'Not found', 'Row not found');
      // Removes a row from the app's own import draft; nothing is deleted in D365.
      d.peopleRows = d.peopleRows.filter((r) => r !== row);
      if (imp.requestId) markEdited(getRequestRecord(imp.requestId), user.upn);
      audit(user.upn, 'people.rowRemoved', imp.requestId, {
        importId,
        sheet: row.sheet,
        rowNumber: row.rowNumber,
      });
      await runValidation(imp, user.upn);
      commit();
      return importView(user, imp);
    },

    async validatePeopleImport(user: CurrentUser, importId: string): Promise<PeopleImportView> {
      const imp = requireImport(importId);
      if (!canEditImport(user, imp)) requireRole(user, ['D365Admin']);
      await runValidation(imp, user.upn);
      commit();
      return importView(user, imp);
    },

    async previewRouting(
      user: CurrentUser,
      input: { importId: string; submitterUpn: string; documentType: DocumentType; amountUsd: number },
    ): Promise<RoutingResult> {
      requireAnyRole(user);
      const imp = requireImport(input.importId);
      const rows = rawRows(imp.id);
      const ctx = await peopleContext(imp, rows);
      const people = buildRoutingPeople(parsePeopleRows(rows).data, ctx);
      return simulateRouting(
        { submitterUpn: input.submitterUpn, documentType: input.documentType, amountUsd: input.amountUsd },
        people,
        ctx.template.routing,
      );
    },

    async peopleTemplateFile(user: CurrentUser, templateId: string): Promise<FileDownload> {
      requireAnyRole(user);
      return {
        fileName: `people-template-${templateId}.xlsx`,
        mimeType: XLSX_MIME,
        bytes: await buildPeopleWorkbook(template(templateId)),
      };
    },

    async sampleFile(user: CurrentUser, requestId: string, variant: SampleVariant): Promise<FileDownload> {
      requireAnyRole(user);
      const req = getRequestRecord(requestId);
      const companyId = req.legalEntityId ?? 'V046';
      return {
        fileName: `people-${variant}-${companyId}.xlsx`,
        mimeType: XLSX_MIME,
        bytes: await buildPeopleWorkbook(
          template(req.templateId),
          samplePeopleRows(variant, companyId, now()),
        ),
      };
    },

    // ── request workbook (one file: intake answers + people sheets) ─────────

    async requestWorkbookFile(
      user: CurrentUser,
      kind: 'blank' | 'example',
      templateId = 'LTPL',
    ): Promise<FileDownload> {
      requireAnyRole(user);
      const tpl = template(templateId);
      const ctx = contextFor(tpl.id);
      if (kind === 'blank')
        return {
          fileName: `venue-request-template-${tpl.id}.xlsx`,
          mimeType: XLSX_MIME,
          bytes: await buildRequestWorkbook({ template: tpl, ctx }),
        };
      const asOf = now();
      return {
        fileName: `venue-request-example-${EXAMPLE_REQUEST_COMPANY}.xlsx`,
        mimeType: XLSX_MIME,
        bytes: await buildRequestWorkbook({
          template: tpl,
          ctx,
          answers: withDefaults(
            {
              ...exampleRequestAnswers(asOf, EXAMPLE_REQUEST_COMPANY, profile.templateCompany),
              'finance.templateId': tpl.id,
            },
            ctx,
          ),
          peopleRows: demoPeopleRows(EXAMPLE_REQUEST_COMPANY, asOf, undefined, profile.templateCompany),
        }),
      };
    },

    /**
     * An existing request as a request file, to reuse for the next venue: its settings and ledger
     * overlays (on their own sheets). Venue identity (name, legal entity, address, EIN) and the
     * people sheets are left blank: they belong to one company, and the copy can't be uploaded as
     * the same company by mistake.
     */
    async requestFile(user: CurrentUser, requestId: string): Promise<FileDownload> {
      requireAnyRole(user);
      const req = getRequestRecord(requestId);
      const ctx = questionContext(req);
      const identity = new Set([
        'venue.name',
        'venue.contractRef',
        'entity.id',
        'entity.legalName',
        'entity.searchName',
        'entity.address',
        'entity.ein',
        'finance.venueDimensionValue',
      ]);
      const answers = Object.fromEntries(
        Object.entries(answersOf(requestId)).filter(([k]) => !identity.has(k)),
      );
      return {
        fileName: `venue-request-from-${req.legalEntityId ?? requestId}.xlsx`,
        mimeType: XLSX_MIME,
        bytes: await buildRequestWorkbook({ template: ctx.template, ctx, answers }),
      };
    },

    // ── workflow hand-off files (manual import in D365) ──────────────────────

    /**
     * Workflow file for the request's company: one XML per workflow (`key`), or a zip of
     * every selected workflow (`key` omitted). Routing from the template, limits and role
     * holders from the latest people import, and the D365 import steps.
     */
    async workflowFile(user: CurrentUser, requestId: string, key?: string): Promise<FileDownload> {
      requireAnyRole(user);
      const req = getRequestRecord(requestId);
      if (!req.legalEntityId)
        throw new ServiceError(400, 'Legal entity ID needed', 'Enter the legal entity ID first.');
      const companyId = req.legalEntityId;
      const tpl = template(req.templateId);
      const answers = answersOf(requestId);
      const imp = latestImportFor(requestId);
      const people = imp ? parsePeopleRows(rawRows(imp.id)).data : null;
      const selected = Array.isArray(answers['workflows.selected'])
        ? (answers['workflows.selected'] as unknown[]).map(String)
        : tpl.workflowLibrary.map((w) => w.key);
      const xmlFor = (wf: TemplateConfig['workflowLibrary'][number]) =>
        buildWorkflowXml({
          workflow: wf,
          template: tpl,
          routing: tpl.routing,
          companyId,
          legalName:
            typeof answers['entity.legalName'] === 'string' ? answers['entity.legalName'] : req.venueName,
          requestId,
          people,
          generatedAt: now(),
        });
      audit(user.upn, 'workflow.fileDownloaded', requestId, { key: key ?? 'all' });
      commit();
      if (key) {
        const wf = tpl.workflowLibrary.find((w) => w.key === key);
        if (!wf) throw new ServiceError(404, 'Not found', `No workflow ${key} in template ${tpl.id}`);
        return {
          fileName: workflowFileName(companyId, wf),
          mimeType: 'application/xml',
          bytes: new TextEncoder().encode(xmlFor(wf)),
        };
      }
      const zip = new JSZip();
      for (const wf of tpl.workflowLibrary.filter((w) => selected.includes(w.key)))
        zip.file(workflowFileName(companyId, wf), xmlFor(wf));
      return {
        fileName: `${companyId}-workflows.zip`,
        mimeType: 'application/zip',
        bytes: await zip.generateAsync({ type: 'uint8array' }),
      };
    },

    /** Request workbook for one RSM test venue (TEST_VENUES), with its own five staff. */
    async testVenueWorkbookFile(user: CurrentUser, companyId: string): Promise<FileDownload> {
      requireAnyRole(user);
      const venue = TEST_VENUES.find((v) => v.companyId === companyId.toUpperCase());
      if (!venue) throw new ServiceError(404, 'Not found', `No test venue ${companyId}`);
      const tpl = template('LTPL');
      const ctx = contextFor(tpl.id);
      const asOf = now();
      return {
        fileName: `venue-request-${venue.companyId}-${venue.city.replace(/\s+/g, '')}.xlsx`,
        mimeType: XLSX_MIME,
        bytes: await buildRequestWorkbook({
          template: tpl,
          ctx,
          answers: withDefaults(testVenueAnswers(venue, asOf, profile.templateCompany), ctx),
          peopleRows: demoPeopleRows(venue.companyId, asOf, testVenueStaff(venue), profile.templateCompany),
        }),
      };
    },

    /**
     * Creates a request from a request workbook: answers from the Request sheet,
     * then the people import from the people sheets. Row-level problems come back
     * as messages and the rest still loads; the wizard shows what is left to fix.
     */
    async importRequestFile(
      user: CurrentUser,
      file: { name: string; bytes: Uint8Array },
    ): Promise<{ request: RequestDetail; peopleImport: PeopleImportView | null; messages: string[] }> {
      requireRole(user, ['Requester']);
      // The content decides (an unreadable file fails parsing); macro-enabled workbooks never load.
      if (/\.(xlsm|xlsb|xls|csv)$/i.test(file.name))
        throw new ServiceError(400, 'Not a request workbook', 'Upload the .xlsx request file');
      const parsed = await parseRequestWorkbook(file.bytes, contextFor('LTPL'));
      if (parsed.fatal) throw new ServiceError(400, 'Not a request workbook', parsed.fatal);
      const messages = [...parsed.messages];
      const venueName = typeof parsed.answers['venue.name'] === 'string' ? parsed.answers['venue.name'] : '';
      if (!venueName)
        throw new ServiceError(400, 'Venue name needed', 'Fill venue.name on the Request sheet');
      // An invalid EIN would fail the whole save; leave it out and say so instead.
      const answers = Object.fromEntries(
        Object.entries(parsed.answers).filter(([key, value]) => {
          const q = QUESTION_BY_KEY.get(key);
          if (!q?.sensitive || !(q.validate?.(value, parsed.answers, contextFor('LTPL')) ?? []).length)
            return true;
          messages.push(`${q.label}: not in the format 12-3456789; left blank`);
          return false;
        }),
      );
      const templateId =
        typeof answers['finance.templateId'] === 'string' ? answers['finance.templateId'] : 'LTPL';
      const created = await service.createRequest(user, { venueName, templateId });
      const id = created.request.id;
      let request = await service.saveAnswers(user, id, answers);
      let peopleImport: PeopleImportView | null = null;
      if (parsed.peopleRows.length) {
        if (request.request.legalEntityId)
          peopleImport = await service.createPeopleImport(user, id, {
            kind: 'rows',
            rows: parsed.peopleRows,
          });
        else
          messages.push(
            'People sheets not loaded: fill entity.id first, then upload them on the People step',
          );
      }
      audit(user.upn, 'request.importedFromFile', id, {
        fileName: file.name,
        answers: Object.keys(answers).length,
        peopleRows: parsed.peopleRows.length,
      });
      commit();
      request = await service.getRequest(user, id);
      return { request, peopleImport, messages };
    },

    // ── readiness testing (build update 2.4) ──────────────────────────────────

    async getReadiness(user: CurrentUser, requestId: string): Promise<ReadinessView> {
      requireAnyRole(user);
      const req = getRequestRecord(requestId);
      // Latest plan; on equal timestamps the one generated last wins.
      const plan = db()
        .readinessPlans.filter((p) => p.requestId === requestId)
        .reduce<ReadinessPlanRecord | null>(
          (best, p) => (!best || p.createdAt >= best.createdAt ? p : best),
          null,
        );
      const runs = db()
        .readinessRuns.filter(
          (r) =>
            !r.campaignId && db().readinessPlans.some((p) => p.id === r.planId && p.requestId === requestId),
        )
        .sort((a, b) => (b.startedAt ?? '').localeCompare(a.startedAt ?? ''))
        .map(runSummary);
      const latest = plan ? (runs.find((r) => r.planId === plan.id) ?? null) : null;
      const results = latest ? db().readinessResults.filter((r) => r.runId === latest.id) : [];
      const countsByLayer: ReadinessView['countsByLayer'] = { L1: 0, L2: 0, L3: 0, L4: 0 };
      for (const c of plan?.plan.cases ?? []) if (!c.demo) countsByLayer[c.layer] += 1;
      const finished = latest && latest.status !== 'Running' && latest.status !== 'Queued';
      const personasOk = !results.some((r) => r.caseId.endsWith('-ACC-04') && r.status !== 'passed');
      const setupDetail: Record<string, string> = {
        vendor: `TST-0001 found in ${req.legalEntityId ?? ''}, not on hold`,
        category: 'Readiness category active in the shared hierarchy',
        numberSequences: 'PO, invoice, journal and project references each have a sequence',
        journal: 'Unposted journal created by setup for this run',
        projectGroup: 'VENUE project group exists',
        personas: personasOk
          ? 'T-ACC-04 passed: every user matches a tested bundle'
          : 'T-ACC-04 did not pass',
      };
      const setup = READINESS_SETUP_DATA.map((s) => ({
        ...s,
        status: finished ? (s.key === 'personas' && !personasOk ? 'missing' : 'ok') : 'notChecked',
        detail: finished ? (setupDetail[s.key] ?? '') : 'Checked by the preflight at the start of a run',
      })) satisfies SetupDataStatus[];
      const canManage = hasAnyRole(user, ['D365Admin']);
      const provisioned = ['Validating', 'Ready'].includes(req.status);
      return {
        requestId,
        company: req.legalEntityId,
        plan,
        countsByLayer,
        runs,
        latestRun: latest,
        results,
        setup,
        cannotGenerate: req.legalEntityId ? null : 'Enter the legal entity ID first.',
        cannotRun: !plan
          ? 'Generate a plan first.'
          : !provisioned
            ? 'Runs in D365 TEST after provisioning.'
            : latest && (latest.status === 'Running' || latest.status === 'Queued')
              ? 'A run is in progress.'
              : null,
        canManage,
      };
    },

    async generateReadinessPlan(
      user: CurrentUser,
      requestId: string,
      environment: ReadinessEnv = 'TEST',
    ): Promise<ReadinessView> {
      requireRole(user, ['D365Admin']);
      const req = getRequestRecord(requestId);
      if (!req.legalEntityId)
        throw new ServiceError(400, 'Legal entity ID needed', 'Enter the legal entity ID first.');
      await createReadinessPlan(req.legalEntityId, req, environment, user.upn);
      commit();
      return service.getReadiness(user, requestId);
    },

    async startReadinessRun(user: CurrentUser, planId: string): Promise<ReadinessRunSummary> {
      requireRole(user, ['D365Admin']);
      const plan = db().readinessPlans.find((p) => p.id === planId);
      if (!plan) throw new ServiceError(404, 'Not found', `Readiness plan ${planId} does not exist`);
      const req = plan.requestId ? getRequestRecord(plan.requestId) : null;
      if (req && !['Validating', 'Ready'].includes(req.status))
        throw new ServiceError(409, 'Not provisioned yet', 'Readiness runs in D365 TEST after provisioning.');
      if (
        db().readinessRuns.some(
          (r) => r.planId === planId && (r.status === 'Running' || r.status === 'Queued'),
        )
      )
        throw new ServiceError(409, 'Run in progress', 'Wait for the current run to finish.');
      const run: ReadinessRunRecord = {
        id: newId(),
        planId,
        campaignId: null,
        label: null,
        environment: plan.environment,
        status: 'Queued',
        d365Version: '10.0.49',
        startedBy: user.upn,
        startedAt: stamp(),
        endedAt: null,
        reportUrl: null,
        runnerRef: 'mock',
      };
      db().readinessRuns.push(run);
      audit(user.upn, 'readiness.runStarted', plan.requestId, {
        runId: run.id,
        planId,
        cases: plan.plan.cases.length,
      });
      commit();
      schedule(() => executeReadinessRun(run.id, { instant: false }));
      return runSummary(run);
    },

    async waiveReadinessResult(
      user: CurrentUser,
      resultId: string,
      reason: string,
    ): Promise<ReadinessResultRecord> {
      requireRole(user, ['D365Admin']);
      const result = db().readinessResults.find((r) => r.id === resultId);
      if (!result) throw new ServiceError(404, 'Not found', `Result ${resultId} does not exist`);
      if (result.status !== 'failed')
        throw new ServiceError(400, 'Not a failure', 'Only failed cases can be waived.');
      const why = reason.trim();
      if (why.length < 5) throw new ServiceError(400, 'Reason needed', 'Say why this failure is accepted.');
      result.waivedBy = user.upn;
      result.waiveReason = why;
      result.waivedAt = stamp();
      const run = db().readinessRuns.find((r) => r.id === result.runId);
      if (run) {
        run.endedAt ??= stamp();
        finishReadinessRun(run);
      }
      const plan = run ? db().readinessPlans.find((p) => p.id === run.planId) : undefined;
      audit(user.upn, 'readiness.resultWaived', plan?.requestId ?? null, {
        caseId: result.caseId,
        reason: why,
      });
      commit();
      return { ...result };
    },

    async getTestingLibrary(user: CurrentUser): Promise<TestingLibraryView> {
      requireAnyRole(user);
      const allRuns = db().readinessRuns;
      const caseTemplate = new Map<string, string>();
      for (const p of db().readinessPlans)
        for (const c of p.plan.cases) caseTemplate.set(`${p.id}|${c.id}`, c.template);
      const planOfRun = new Map(allRuns.map((r) => [r.id, r.planId]));
      const healer = new Map<string, number>();
      const stats = new Map<
        string,
        { passed: number; failed: number; runs: Set<string>; last: string | null }
      >();
      for (const r of db().readinessResults) {
        const run = allRuns.find((x) => x.id === r.runId);
        const tid = caseTemplate.get(`${planOfRun.get(r.runId) ?? ''}|${r.caseId}`);
        if (!tid || !run || r.caseId.endsWith('-DEMO')) continue;
        const s = stats.get(tid) ?? { passed: 0, failed: 0, runs: new Set<string>(), last: null };
        if (r.status === 'passed') s.passed += 1;
        if (r.status === 'failed') s.failed += 1;
        s.runs.add(r.runId);
        if (run.startedAt && (!s.last || run.startedAt > s.last)) s.last = run.startedAt;
        stats.set(tid, s);
        // A control that moved in an update is a candidate for a reviewed healer fix (R4).
        if (r.failureKind === 'UiChange') healer.set(tid, (healer.get(tid) ?? 0) + 1);
      }
      const casesPer = new Map<string, number>();
      for (const t of caseTemplate.values()) casesPer.set(t, (casesPer.get(t) ?? 0) + 1);
      const sample = seededVenuePeopleRows('V001', now());
      const seats = sample
        ? personaSeats(
            'V001',
            parsePeopleRows(
              sample.map((r, i) => ({ id: `p${i}`, sheet: r.sheet, rowNumber: i + 2, data: r.data })),
            ).data,
          )
        : [];
      const roleName = (id: string) => template('LTPL').roleCatalog.find((r) => r.roleId === id)?.name ?? id;
      return {
        templates: READINESS_TEMPLATES.map((t) => {
          const s = stats.get(t.id);
          const decided = s ? s.passed + s.failed : 0;
          return {
            ...t,
            cases: casesPer.get(t.id) ?? 0,
            runs: s?.runs.size ?? 0,
            passRate: s && decided ? s.passed / decided : null,
            lastRunAt: s?.last ?? null,
            healerProposals: healer.get(t.id) ?? 0,
          };
        }),
        personas: READINESS_PERSONAS.map((p) => {
          const bundle = seats.find((s) => s.persona.key === p.key)?.roles ?? [];
          return { ...p, account: personaAccount(p.key), bundle, bundleNames: bundle.map(roleName) };
        }),
        dataSets: READINESS_DATA_SETS,
        setupData: READINESS_SETUP_DATA,
        runs: allRuns.map(runSummary).sort((a, b) => (b.startedAt ?? '').localeCompare(a.startedAt ?? '')),
      };
    },

    async listCampaigns(user: CurrentUser): Promise<CampaignView[]> {
      requireAnyRole(user);
      const venues = await deps.d365.listVenueCompanies();
      const venueNames = Object.fromEntries(venues.map((v) => [v.id, v.name]));
      const comparable = (runIds: readonly string[]): ComparableResult[] =>
        runIds.flatMap((runId) => {
          const run = db().readinessRuns.find((r) => r.id === runId);
          const plan = run ? db().readinessPlans.find((p) => p.id === run.planId) : undefined;
          if (!plan) return [];
          return db()
            .readinessResults.filter((r) => r.runId === runId)
            .flatMap((r) => {
              const c = plan.plan.cases.find((x) => x.id === r.caseId);
              return c
                ? [
                    {
                      caseId: r.caseId,
                      company: plan.companyId,
                      layer: c.layer,
                      template: c.template,
                      status: r.status,
                      failureKind: r.failureKind,
                      errorSummary: r.errorSummary,
                      traceUrl: r.traceUrl,
                    },
                  ]
                : [];
            });
        });
      return db()
        .campaigns.slice()
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .map((campaign) => {
          const runsOf = (ids: readonly string[]) =>
            ids.flatMap((id) => {
              const r = db().readinessRuns.find((x) => x.id === id);
              return r ? [runSummary(r)] : [];
            });
          return {
            campaign: structuredClone(campaign),
            baselineRuns: runsOf(campaign.baselineRunIds),
            candidateRuns: runsOf(campaign.candidateRunIds),
            comparison: campaign.candidateRunIds.length
              ? compareRuns(comparable(campaign.baselineRunIds), comparable(campaign.candidateRunIds))
              : null,
            venueNames,
          };
        });
    },

    /** Active templates to choose from on New request. */
    async listTemplates(user: CurrentUser) {
      requireAnyRole(user);
      return db()
        .templates.filter((t) => t.active)
        .map((t) => ({
          id: t.id,
          templateLegalEntityId: t.templateLegalEntityId,
          label: TEMPLATE_LABELS[t.id] ?? `${t.id} template company`,
          defaults: templateDefaults(t.id),
        }));
    },

    async listVenues(user: CurrentUser) {
      requireAnyRole(user);
      return deps.d365.listVenueCompanies();
    },

    async listTemplateVendors(user: CurrentUser, templateId: string, query?: string) {
      requireAnyRole(user);
      return deps.d365.listVendors(template(templateId).templateLegalEntityId, query);
    },

    // ── data packages ───────────────────────────────────────────────────────

    async getDataPackages(
      user: CurrentUser,
      requestId: string,
    ): Promise<{
      setup: DataPackage;
      people: DataPackage | null;
      projects: DataPackage | null;
      ledger: DataPackage;
      companyId: string;
    }> {
      requireAnyRole(user);
      const req = getRequestRecord(requestId);
      const companyId = req.legalEntityId ?? 'NEW';
      const imp = latestImportFor(requestId);
      const data = imp ? parsePeopleRows(rawRows(imp.id)).data : null;
      const answers = answersOf(requestId);
      const legalName = typeof answers['entity.legalName'] === 'string' ? answers['entity.legalName'] : '';
      return {
        setup: buildSetupPackage(answers, companyId),
        people: data
          ? buildPeoplePackage(data, companyId, {
              asOf: now().toISOString().slice(0, 10),
              env: peoplePackageEnv(profile),
            })
          : null,
        projects: data ? buildProjectsPackage(data, companyId) : null,
        ledger: buildTemplateLedgerPackage(companyId, {
          legalName,
          asOf: now(),
          profile: templateProfileFor(profile.templateCompany),
          overlays: ledgerOverlaysOf(answers),
        }),
        companyId,
      };
    },

    async downloadDataPackage(user: CurrentUser, requestId: string, which: PackageId): Promise<FileDownload> {
      const packages = await service.getDataPackages(user, requestId);
      const pkg = packages[which];
      const { companyId } = packages;
      if (!pkg)
        throw new ServiceError(
          404,
          'Not found',
          which === 'projects'
            ? 'This request has no customers, vendors or projects yet'
            : 'This request has no people import yet',
        );
      audit(user.upn, 'package.downloaded', requestId, { package: pkg.definitionGroup });
      commit();
      return {
        fileName: packageFileName(pkg, companyId),
        mimeType: 'application/zip',
        bytes: await zipPackage(pkg),
      };
    },

    // ── submit, approvals, confirm, cancel (spec 7) ─────────────────────────

    async submitRequest(user: CurrentUser, id: string): Promise<RequestDetail> {
      const req = getRequestRecord(id);
      applyTransition(req, { type: 'submit' }, user, { gate1: await gate1For(req) });
      commit();
      return service.getRequest(user, id);
    },

    async decide(
      user: CurrentUser,
      id: string,
      input: { gate: 2 | 3; decision: 'Approved' | 'SentBack'; comment?: string },
    ): Promise<RequestDetail> {
      const req = getRequestRecord(id);
      const comment = input.comment?.trim() ?? '';
      const ctx: LifecycleContext = {
        approvals: db().approvals.filter((a) => a.requestId === id),
        dryRunFresh: dryRunFresh(req),
      };
      const action: LifecycleAction =
        input.decision === 'Approved'
          ? { type: 'approve', gate: input.gate }
          : { type: 'sendBack', gate: input.gate, comment };
      applyTransition(req, action, user, ctx, { gate: input.gate, ...(comment ? { comment } : {}) });
      db().approvals.push({
        id: newId(),
        requestId: id,
        gate: input.gate,
        approverUpn: user.upn,
        decision: input.decision,
        comment: comment || null,
        requestVersion: req.version,
        decidedAt: stamp(),
      });
      commit();
      return service.getRequest(user, id);
    },

    async confirmRequest(user: CurrentUser, id: string, typedId: string): Promise<RunView> {
      const req = getRequestRecord(id);
      applyTransition(req, { type: 'confirm', typedId }, user, { gate1: await gate1For(req) });
      const imp = latestImportFor(id);
      const answers = answersOf(id);
      const run: RunRecord = {
        id: newId(),
        requestId: id,
        peopleImportId: imp?.id ?? null,
        kind: 'ProvisionVenue',
        answersSnapshot: { ...answers, __deviations: deviations(answers, questionContext(req)) },
        peopleSnapshot: imp
          ? rowsOf(imp.id).map((r) => ({
              sheet: r.sheet,
              rowNumber: r.rowNumber,
              data: structuredClone(r.data),
            }))
          : [],
        startedBy: user.upn,
        startedAt: stamp(),
        endedAt: null,
        status: 'Running',
        orchestrationInstanceId: `browser-${newId().slice(0, 8)}`,
      };
      db().runs.push(run);
      PROVISION_PLAN.forEach((stepKey, i) =>
        db().steps.push({
          id: newId(),
          runId: run.id,
          stepKey,
          sequence: i + 1,
          status: 'Pending',
          attempt: 0,
          d365ExecutionId: null,
          output: null,
          error: null,
          startedAt: null,
          endedAt: null,
        }),
      );
      if (imp) imp.status = 'Applying';
      // Manual tasks from answers (spec 11.1): out-of-scope items become tracked tasks.
      const fromAnswers: Array<[string, string, string]> = [
        [
          'finance.openingBalances',
          'opening-balances',
          `Load opening balances into ${req.legalEntityId ?? ''}`,
        ],
        ['finance.bankAccounts', 'bank-accounts', `Set up bank accounts for ${req.legalEntityId ?? ''}`],
        [
          'finance.intercompany',
          'intercompany',
          `Set up intercompany with the management company for ${req.legalEntityId ?? ''}`,
        ],
      ];
      for (const [key, taskKey, description] of fromAnswers) {
        if (answers[key] === true)
          addTask(req, req.requesterUpn, { taskKey, description, ownerUpn: null, dueDate: null });
      }
      audit(user.upn, 'run.started', id, { runId: run.id });
      commit();
      schedule(() => driveRun(run.id));
      return runView(run.id);
    },

    async cancelRequest(user: CurrentUser, id: string): Promise<RequestDetail> {
      applyTransition(getRequestRecord(id), { type: 'cancel' }, user);
      commit();
      return service.getRequest(user, id);
    },

    // ── provisioning ─────────────────────────────────────────────────────────

    async getLatestRun(user: CurrentUser, requestId: string): Promise<RunView | null> {
      requireAnyRole(user);
      const run = db()
        .runs.filter((r) => r.requestId === requestId)
        .sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0];
      return run ? runView(run.id) : null;
    },

    async resumeRun(user: CurrentUser, runId: string): Promise<RunView> {
      const run = db().runs.find((r) => r.id === runId);
      if (!run) throw new ServiceError(404, 'Not found', 'Run not found');
      if (run.status !== 'NeedsAttention')
        throw new ServiceError(409, 'Not allowed', 'Only runs that need attention can resume');
      if (run.requestId)
        applyTransition(getRequestRecord(run.requestId), { type: 'resume' }, user, {}, { runId });
      else requireRole(user, ['D365Admin']);
      for (const s of db().steps.filter((x) => x.runId === runId && x.status === 'Failed')) {
        s.status = 'Pending';
      }
      run.status = 'Running';
      audit(user.upn, 'run.resumed', run.requestId, { runId });
      commit();
      schedule(() => driveRun(runId));
      return runView(runId);
    },

    /** Await a run to completion or a blocking failure (tests and server-side callers). */
    async driveRun(runId: string): Promise<void> {
      await driveRun(runId);
    },

    async getReport(user: CurrentUser, requestId: string): Promise<ValidationReport | null> {
      requireAnyRole(user);
      const run = db()
        .runs.filter((r) => r.requestId === requestId)
        .sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0];
      const step = run && db().steps.find((s) => s.runId === run.id && s.stepKey === 'validate');
      const report = step?.output?.['report'] as ValidationReport | undefined;
      if (!report) return null;
      // Re-evaluate the manual-task check live: tasks change after the report.
      const tasks = tasksOf(requestId);
      const incomplete = tasks.filter((t) => !t.ownerUpn || !t.dueDate);
      return {
        ...report,
        checks: report.checks.map((c) =>
          c.key === 'manual-tasks'
            ? {
                ...c,
                status: incomplete.length ? 'fail' : 'pass',
                detail: `${tasks.length} tasks; ${incomplete.length ? `${incomplete.length} without owner or due date` : 'all have an owner and due date'}`,
              }
            : c,
        ),
      };
    },

    async updateTask(
      user: CurrentUser,
      requestId: string,
      taskId: string,
      patch: { status?: 'Open' | 'Done'; ownerUpn?: string; dueDate?: string },
    ): Promise<TaskRecord[]> {
      requireRole(user, ['D365Admin']);
      const task = tasksOf(requestId).find((t) => t.id === taskId);
      if (!task) throw new ServiceError(404, 'Not found', 'Task not found');
      if (patch.status) task.status = patch.status;
      if (patch.ownerUpn !== undefined) task.ownerUpn = patch.ownerUpn.trim().toLowerCase() || null;
      if (patch.dueDate !== undefined) task.dueDate = patch.dueDate || null;
      audit(user.upn, 'task.updated', requestId, { taskKey: task.taskKey, ...patch });
      commit();
      return tasksOf(requestId);
    },

    async completeRequest(user: CurrentUser, id: string): Promise<RequestDetail> {
      const req = getRequestRecord(id);
      const report = await service.getReport(user, id);
      applyTransition(req, { type: 'complete' }, user, {
        allTasksDone: tasksOf(id).every((t) => t.status === 'Done'),
        checksPass: !!report && report.checks.every((c) => c.status !== 'fail'),
      });
      commit();
      return service.getRequest(user, id);
    },

    async listAudit(user: CurrentUser, requestId: string): Promise<AuditRecord[]> {
      requireAnyRole(user);
      return db()
        .audit.filter((a) => a.requestId === requestId)
        .sort((a, b) => b.at.localeCompare(a.at));
    },
  };
  return service;
}

export type AppService = ReturnType<typeof createAppService>;
